// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import { showSnackBar } from "../../snackbar/actions"
import * as TimelineSlice from "../../timeline/slices/timeline"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { Code, executorMutation, Init, isConnectError, timesheetFieldService } from "../../../app/rpc"
import {
  CreateTimesheetFieldRequestSchema,
  ListTimesheetFieldOptionsRequestSchema,
  TimesheetField,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"

type CreateTimesheetFieldRpc = (request: Init<typeof CreateTimesheetFieldRequestSchema>) => Observable<TimesheetField>

export const createTimesheetField = (createTimesheetFieldRpc: CreateTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.createTimesheetField.match),
      map(action => action.payload),
      mergeMap(payload =>
        createTimesheetFieldRpc(payload.request).pipe(
          mergeMap(response => {
            const timesheetField = response
            if (payload.onSuccess) {
              payload.onSuccess()
            }
            return concat([
              Slice.addNewFieldId(timesheetField.id),
              TimelineSlice.setTimesheetField(timesheetField),
              Actions.listTimesheetFieldOptions(
                create(ListTimesheetFieldOptionsRequestSchema, {
                  siteId: timesheetField.siteId,
                  timesheetFieldId: timesheetField.id,
                })
              ),
            ])
          }),
          catchError(handleError),
        )
      ),
    )

const handleError = (e: unknown) => {
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      return of(showSnackBar({
        message: t({
          id: "msg_create_timesheet_field_permission_denied",
          message: "Failed to create timesheet field. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_create_timesheet_field_failed",
      message: "Failed to create timesheet field. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const createTimesheetFieldRpc = (request: Init<typeof CreateTimesheetFieldRequestSchema>): Observable<TimesheetField> =>
  executorMutation(() => timesheetFieldService.createTimesheetField(create(CreateTimesheetFieldRequestSchema, request)))

export default createTimesheetField(createTimesheetFieldRpc)
